"use client";

import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import type { ServerSubmission } from "@/lib/supabase/server-services";

function submissionDuration(submission: ServerSubmission): number {
	if (submission.audio_asset?.duration_ms != null) {
		return submission.audio_asset.duration_ms / 1000;
	}
	return submission.embed_metadata?.duration_seconds ?? 0;
}

type PlaybackCommand = {
	action: "play" | "pause";
	nonce: number;
};

type SeekCommand = {
	submissionId: number;
	time: number;
	nonce: number;
};

interface GlobalPlayerState {
	submission: ServerSubmission | null;
	activeSubmissionId: number | null;
	isOpen: boolean;
	isPlaying: boolean;
	currentTime: number;
	duration: number;
	playbackCommand: PlaybackCommand | null;
	seekCommand: SeekCommand | null;
}

interface GlobalPlayerContextValue extends GlobalPlayerState {
	openPlayer: (submission: ServerSubmission) => void;
	closePlayer: () => void;
	pausePlayer: () => void;
	playPlayer: () => void;
	seekPlayer: (submission: ServerSubmission, time: number) => void;
	setPlayerProgress: (
		submissionId: number,
		currentTime: number,
		duration: number,
	) => void;
	setPlayerPlaying: (isPlaying: boolean) => void;
	togglePlayer: (submission: ServerSubmission) => void;
}

const GlobalPlayerContext = createContext<GlobalPlayerContextValue | null>(
	null,
);

export function GlobalPlayerProvider({ children }: { children: ReactNode }) {
	const [state, setState] = useState<GlobalPlayerState>({
		submission: null,
		activeSubmissionId: null,
		isOpen: false,
		isPlaying: false,
		currentTime: 0,
		duration: 0,
		playbackCommand: null,
		seekCommand: null,
	});

	const openPlayer = useCallback((submission: ServerSubmission) => {
		setState((prev) => {
			const sameSubmission = prev.activeSubmissionId === submission.id;
			return {
				...prev,
				submission,
				activeSubmissionId: submission.id,
				isOpen: true,
				isPlaying: sameSubmission ? prev.isPlaying : false,
				currentTime: sameSubmission ? prev.currentTime : 0,
				duration: sameSubmission
					? prev.duration
					: submissionDuration(submission),
				playbackCommand: {
					action: "play",
					nonce: (prev.playbackCommand?.nonce ?? 0) + 1,
				},
			};
		});
	}, []);

	const closePlayer = useCallback(() => {
		setState((prev) => ({
			...prev,
			isOpen: false,
			isPlaying: false,
			playbackCommand: {
				action: "pause",
				nonce: (prev.playbackCommand?.nonce ?? 0) + 1,
			},
		}));
	}, []);

	const pausePlayer = useCallback(() => {
		setState((prev) => ({
			...prev,
			isPlaying: false,
			playbackCommand: {
				action: "pause",
				nonce: (prev.playbackCommand?.nonce ?? 0) + 1,
			},
		}));
	}, []);

	const playPlayer = useCallback(() => {
		setState((prev) => ({
			...prev,
			isOpen: prev.submission ? true : prev.isOpen,
			playbackCommand: {
				action: "play",
				nonce: (prev.playbackCommand?.nonce ?? 0) + 1,
			},
		}));
	}, []);

	const setPlayerPlaying = useCallback((isPlaying: boolean) => {
		setState((prev) =>
			prev.isPlaying === isPlaying ? prev : { ...prev, isPlaying },
		);
	}, []);

	const setPlayerProgress = useCallback(
		(submissionId: number, currentTime: number, duration: number) => {
			setState((prev) =>
				prev.activeSubmissionId !== submissionId ||
				(prev.currentTime === currentTime && prev.duration === duration)
					? prev
					: { ...prev, currentTime, duration },
			);
		},
		[],
	);

	const seekPlayer = useCallback(
		(submission: ServerSubmission, time: number) => {
			setState((prev) => {
				const sameSubmission = prev.activeSubmissionId === submission.id;
				return {
					...prev,
					submission,
					activeSubmissionId: submission.id,
					isOpen: true,
					isPlaying: sameSubmission ? prev.isPlaying : false,
					currentTime: Math.max(0, time),
					duration: sameSubmission
						? prev.duration
						: submissionDuration(submission),
					seekCommand: {
						submissionId: submission.id,
						time: Math.max(0, time),
						nonce: (prev.seekCommand?.nonce ?? 0) + 1,
					},
					playbackCommand: sameSubmission
						? prev.playbackCommand
						: {
								action: "play",
								nonce: (prev.playbackCommand?.nonce ?? 0) + 1,
							},
				};
			});
		},
		[],
	);

	const togglePlayer = useCallback((submission: ServerSubmission) => {
		setState((prev) => {
			const sameSubmission = prev.activeSubmissionId === submission.id;
			const nextAction: PlaybackCommand["action"] =
				sameSubmission && prev.isPlaying ? "pause" : "play";

			return {
				...prev,
				submission,
				activeSubmissionId: submission.id,
				isOpen: true,
				isPlaying:
					nextAction === "pause"
						? false
						: sameSubmission
							? prev.isPlaying
							: false,
				currentTime: sameSubmission ? prev.currentTime : 0,
				duration: sameSubmission
					? prev.duration
					: submissionDuration(submission),
				playbackCommand: {
					action: nextAction,
					nonce: (prev.playbackCommand?.nonce ?? 0) + 1,
				},
			};
		});
	}, []);

	const value = useMemo<GlobalPlayerContextValue>(
		() => ({
			...state,
			openPlayer,
			closePlayer,
			pausePlayer,
			playPlayer,
			seekPlayer,
			setPlayerProgress,
			setPlayerPlaying,
			togglePlayer,
		}),
		[
			state,
			openPlayer,
			closePlayer,
			pausePlayer,
			playPlayer,
			seekPlayer,
			setPlayerProgress,
			setPlayerPlaying,
			togglePlayer,
		],
	);

	return (
		<GlobalPlayerContext.Provider value={value}>
			{children}
		</GlobalPlayerContext.Provider>
	);
}

export function useGlobalPlayer(): GlobalPlayerContextValue {
	const ctx = useContext(GlobalPlayerContext);
	if (!ctx) {
		throw new Error("useGlobalPlayer must be used within GlobalPlayerProvider");
	}
	return ctx;
}
